import { z } from 'zod';
import { type Currency, isCurrency } from './currency.js';
import type { PaymentStatus } from './donation-status.js';
import { DomainError, PricingViolationError } from './errors.js';
import { type Money, RATE_BASE_CURRENCY } from './money.js';

/**
 * How a campaign prices a donation. `free` is **the donor chooses the amount**,
 * not "costs nothing"; `no_charge` is the service that is requested rather than
 * paid for — a hatim — and takes no money at all:
 * docs/decisions/0073-a-campaign-may-take-no-money-and-its-yurt-is-told.md.
 */
export const CAMPAIGN_PRICING_MODES = ['free', 'suggested', 'unit', 'no_charge'] as const;
export type CampaignPricingMode = (typeof CAMPAIGN_PRICING_MODES)[number];
export const campaignPricingModeSchema = z.enum(CAMPAIGN_PRICING_MODES);

/**
 * Whether a campaign's donations owe money. Every surface that shows an amount,
 * a currency or a payment step asks this of the campaign's own mode, never of
 * its name.
 */
export function chargesMoney(mode: CampaignPricingMode): boolean {
  return mode !== 'no_charge';
}

/**
 * The financial axis a donation is born on: `unpaid` owes money that has not
 * come, `not_charged` owes none and never moves. It follows the campaign's
 * mode and nothing else, and the database refuses a row where the two
 * disagree.
 */
export function paymentStatusAtEntry(mode: CampaignPricingMode): PaymentStatus {
  return chargesMoney(mode) ? 'unpaid' : 'not_charged';
}

/**
 * The currency a no-charge donation's zero is recorded in. A zero is the same
 * in every currency, the column still needs one, and the base currency is the
 * one whose base amount is itself with no exchange rate — so an entry never
 * waits on a rate that could not change anything, and every request of a
 * season lands in one totals row.
 */
export const NO_CHARGE_CURRENCY: Currency = RATE_BASE_CURRENCY;

/** The range a `bigint` minor-unit column can hold. */
export const MAX_MINOR_AMOUNT = 9_223_372_036_854_775_807n;

export type CampaignPrice = {
  readonly currency: string;
  readonly unitAmountMinor?: bigint | null;
  readonly minAmountMinor?: bigint | null;
  readonly maxAmountMinor?: bigint | null;
};

export type PriceDonationInput = {
  readonly mode: CampaignPricingMode;
  readonly price: CampaignPrice;
  readonly quantity?: number | null;
  readonly amountMinor?: bigint | number | string | null;
};

function assertSupportedCurrency(currency: string): Currency {
  if (!isCurrency(currency)) {
    throw new PricingViolationError('CURRENCY_UNSUPPORTED', { currency });
  }
  return currency;
}

function assertPositiveIntegerQuantity(quantity: number | null | undefined): bigint {
  if (quantity == null || !Number.isFinite(quantity) || !Number.isInteger(quantity)) {
    throw new PricingViolationError('QUANTITY_INVALID', { quantity: String(quantity) });
  }
  if (quantity < 1 || !Number.isSafeInteger(quantity)) {
    throw new PricingViolationError('QUANTITY_INVALID', { quantity: String(quantity) });
  }
  return BigInt(quantity);
}

/**
 * The single rounding point of the pricing path: a minor-unit amount that
 * arrives as a fractional number is rounded half-up, away from zero, and
 * nothing downstream rounds again.
 */
function toMinorHalfUp(value: bigint | number | string): bigint {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new PricingViolationError('AMOUNT_OUT_OF_RANGE', { amountMinor: String(value) });
    }
    if (!Number.isSafeInteger(value) && Math.abs(value) > Number.MAX_SAFE_INTEGER) {
      throw new PricingViolationError('AMOUNT_OUT_OF_RANGE', { amountMinor: String(value) });
    }
    const rounded = value < 0 ? -Math.round(-value) : Math.round(value);
    return BigInt(rounded);
  }
  return toMinorHalfUp(Number(value));
}

function assertWithinColumnRange(amountMinor: bigint): bigint {
  if (amountMinor < 0n || amountMinor > MAX_MINOR_AMOUNT) {
    throw new PricingViolationError('AMOUNT_OUT_OF_RANGE', { amountMinor: amountMinor.toString() });
  }
  return amountMinor;
}

function assertWithinBounds(amountMinor: bigint, price: CampaignPrice): bigint {
  const min = price.minAmountMinor;
  const max = price.maxAmountMinor;
  if (min != null && amountMinor < min) {
    throw new PricingViolationError('AMOUNT_BELOW_MINIMUM', {
      amountMinor: amountMinor.toString(),
      minAmountMinor: min.toString(),
      currency: price.currency,
    });
  }
  if (max != null && amountMinor > max) {
    throw new PricingViolationError('AMOUNT_ABOVE_MAXIMUM', {
      amountMinor: amountMinor.toString(),
      maxAmountMinor: max.toString(),
      currency: price.currency,
    });
  }
  return amountMinor;
}

/**
 * The only place a donation amount is computed. `unit` multiplies the stored
 * unit price by the quantity and ignores any client-supplied total; `free` and
 * `suggested` accept the donor's amount and reject it outside the period's
 * `[min, max]` bounds; `no_charge` is zero in `NO_CHARGE_CURRENCY` whatever
 * currency and quantity were asked, needs no price row, and refuses any amount
 * but zero with `AMOUNT_NOT_CHARGED` rather than quietly dropping it.
 */
export function priceDonation(input: PriceDonationInput): Money {
  if (input.mode === 'no_charge') return priceNoCharge(input.amountMinor);

  const currency = assertSupportedCurrency(input.price.currency);

  if (input.mode === 'unit') {
    const unitAmountMinor = input.price.unitAmountMinor;
    if (unitAmountMinor == null) {
      throw new PricingViolationError('UNIT_PRICE_MISSING', { currency });
    }
    if (unitAmountMinor < 0n) {
      throw new PricingViolationError('AMOUNT_OUT_OF_RANGE', {
        unitAmountMinor: unitAmountMinor.toString(),
      });
    }
    const quantity = assertPositiveIntegerQuantity(input.quantity);
    return {
      amountMinor: assertWithinColumnRange(unitAmountMinor * quantity),
      currency,
    };
  }

  if (input.amountMinor == null) {
    throw new PricingViolationError('AMOUNT_REQUIRED', { mode: input.mode });
  }

  const amountMinor = assertWithinColumnRange(toMinorHalfUp(input.amountMinor));
  return { amountMinor: assertWithinBounds(amountMinor, input.price), currency };
}

function priceNoCharge(amountMinor: PriceDonationInput['amountMinor']): Money {
  if (amountMinor != null && toMinorHalfUp(amountMinor) !== 0n) {
    throw new PricingViolationError('AMOUNT_NOT_CHARGED', { amountMinor: String(amountMinor) });
  }
  return { amountMinor: 0n, currency: NO_CHARGE_CURRENCY };
}

export const CAMPAIGN_PRICE_FIELDS = [
  'unitAmountMinor',
  'suggestedAmountsMinor',
  'minAmountMinor',
  'maxAmountMinor',
] as const;
export type CampaignPriceField = (typeof CAMPAIGN_PRICE_FIELDS)[number];

/**
 * Which columns of a price row a pricing mode reads. The editor renders these
 * and the server nulls the rest, so a row never carries a figure its mode
 * ignores — see docs/decisions/0013-campaign-pricing-and-currency.md.
 */
export const PRICE_FIELDS_BY_MODE: Record<CampaignPricingMode, readonly CampaignPriceField[]> = {
  unit: ['unitAmountMinor'],
  suggested: ['suggestedAmountsMinor', 'minAmountMinor', 'maxAmountMinor'],
  free: ['minAmountMinor', 'maxAmountMinor'],
  no_charge: [],
};

const REQUIRED_PRICE_FIELD_REASON: Partial<Record<CampaignPriceField, string>> = {
  unitAmountMinor: 'CAMPAIGN_PRICE_UNIT_REQUIRED',
  suggestedAmountsMinor: 'CAMPAIGN_PRICE_SUGGESTED_REQUIRED',
};

const REQUIRED_PRICE_FIELDS: Record<CampaignPricingMode, readonly CampaignPriceField[]> = {
  unit: ['unitAmountMinor'],
  suggested: ['suggestedAmountsMinor'],
  free: [],
  no_charge: [],
};

export type CampaignPriceRow = {
  readonly unitAmountMinor: bigint | null;
  readonly minAmountMinor: bigint | null;
  readonly maxAmountMinor: bigint | null;
  readonly suggestedAmountsMinor: readonly bigint[] | null;
};

function priceRuleViolation(reason: string, details: Record<string, unknown> = {}): DomainError {
  return new DomainError('VALIDATION_FAILED', `Price row rejected: ${reason}`, {
    reason,
    ...details,
  });
}

function isPresent(price: CampaignPriceRow, field: CampaignPriceField): boolean {
  const value = price[field];
  if (value === null) return false;
  if (Array.isArray(value)) return value.length > 0 && value.every((amount) => amount > 0n);
  return (value as bigint) > 0n;
}

/**
 * Reduces a price row to the columns its mode reads, nulling the others, and
 * refuses a row that lacks what the mode needs: `unit` a positive unit price,
 * `suggested` at least one positive chip inside `[min, max]`. Bounds are
 * checked in every mode that carries them.
 */
export function normalizePriceForMode(
  mode: CampaignPricingMode,
  price: CampaignPriceRow,
): CampaignPriceRow {
  const read = new Set(PRICE_FIELDS_BY_MODE[mode]);
  const normalized: CampaignPriceRow = {
    unitAmountMinor: read.has('unitAmountMinor') ? price.unitAmountMinor : null,
    minAmountMinor: read.has('minAmountMinor') ? price.minAmountMinor : null,
    maxAmountMinor: read.has('maxAmountMinor') ? price.maxAmountMinor : null,
    suggestedAmountsMinor: read.has('suggestedAmountsMinor') ? price.suggestedAmountsMinor : null,
  };

  for (const field of REQUIRED_PRICE_FIELDS[mode]) {
    if (!isPresent(normalized, field)) {
      throw priceRuleViolation(REQUIRED_PRICE_FIELD_REASON[field] ?? 'CAMPAIGN_PRICE_INVALID', {
        field,
      });
    }
  }

  const { minAmountMinor: min, maxAmountMinor: max } = normalized;
  if (min !== null && max !== null && min > max) {
    throw priceRuleViolation('CAMPAIGN_PRICE_BOUNDS_INVALID', {
      minAmountMinor: min.toString(),
      maxAmountMinor: max.toString(),
    });
  }

  const outside = (normalized.suggestedAmountsMinor ?? []).find(
    (amount) => (min !== null && amount < min) || (max !== null && amount > max),
  );
  if (outside !== undefined) {
    throw priceRuleViolation('CAMPAIGN_PRICE_SUGGESTED_OUT_OF_BOUNDS', {
      amountMinor: outside.toString(),
    });
  }

  return normalized;
}
